import { CategoryResult } from '../types/CategoryResult';

/**
 * Match categories against search query
 *
 * Simple matching logic for dev environment.
 * In production, this would be replaced with more sophisticated
 * matching from Bitrix or search engine.
 */
export class CategoryMatcher {
  /**
   * Match categories to search query
   *
   * Rules:
   * 1. Case-insensitive substring matching in category title
   * 2. Return top N matches (configurable)
   * 3. No complex scoring or ranking
   * 4. Empty query returns empty array (no default categories)
   *
   * @param categories All available categories
   * @param query Search query string
   * @param maxResults Maximum number of categories to return (default: 7)
   * @returns Array of matched categories
   */
  match(
    categories: CategoryResult[],
    query: string,
    maxResults: number = 7
  ): CategoryResult[] {
    // Return empty array for empty query
    if (!query || query.trim() === '') {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();

    // Debug logging for category matching (dev-only layer)
    console.log('[CategoryMatcher] Query:', normalizedQuery, 'Categories count:', categories.length);

    // Filter categories where title contains query
    const matchedCategories = categories.filter(category =>
      category.title.toLowerCase().includes(normalizedQuery)
    );

    console.log('[CategoryMatcher] Matched:', matchedCategories.length, 'categories');
    if (matchedCategories.length > 0) {
      matchedCategories.forEach((cat, i) => {
        console.log(`[CategoryMatcher] ${i + 1}. "${cat.title}" (contains "${normalizedQuery}" at position ${cat.title.toLowerCase().indexOf(normalizedQuery)})`);
      });
    }

    // Sort by relevance (simple: shorter titles first, then alphabetical)
    matchedCategories.sort((a, b) => {
      // First by title length (shorter titles are often more general/better matches)
      const lengthDiff = a.title.length - b.title.length;
      if (lengthDiff !== 0) {
        return lengthDiff;
      }

      // Then alphabetically
      return a.title.localeCompare(b.title);
    });

    // Return limited results
    return matchedCategories.slice(0, maxResults);
  }

  /**
   * Alternative matching: find categories by parent hierarchy
   * Useful for showing related categories
   */
  matchByParent(
    categories: CategoryResult[],
    parentId: string | number
  ): CategoryResult[] {
    return categories.filter(category => category.parentId === parentId);
  }

  /**
   * Get root categories (level 1)
   */
  getRootCategories(categories: CategoryResult[]): CategoryResult[] {
    return categories.filter(category => category.level === 1);
  }

  /**
   * Get categories by level
   */
  getCategoriesByLevel(categories: CategoryResult[], level: number): CategoryResult[] {
    return categories.filter(category => category.level === level);
  }
}

// Export singleton instance
export const categoryMatcher = new CategoryMatcher();